/**
 * Decode a TIFF / GeoTIFF File to a canvas and optionally extract geographic bounds.
 *
 * Uses `utif` (CommonJS, webpack 4 compatible).
 *
 * Returns: { canvas, bounds, width, height }
 *   bounds — [[south, west], [north, east]] when GeoTIFF metadata is detected,
 *            null otherwise (caller falls back to current map view).
 */
import UTIF from 'utif';

// ── GeoTIFF tag numbers ──────────────────────────────────────────────────────
const TAG_MODEL_PIXEL_SCALE    = 33550; // [ScaleX, ScaleY, ScaleZ]
const TAG_MODEL_TIEPOINT       = 33922; // [I,J,K, X,Y,Z, ...] pairs
const TAG_GEO_KEY_DIRECTORY    = 34736; // GeoKey integer params
const TAG_GEO_ASCII_PARAMS     = 34737; // GeoKey string params

// GeoKey IDs for CRS identification
const GEO_KEY_GEOGRAPHIC_TYPE   = 2048;
const GEO_KEY_PROJECTED_CS_TYPE = 3072;

// ── WebMercator (EPSG:3857) → WGS84 (EPSG:4326) ─────────────────────────────
function mercatorToLatLng(mx, my) {
  const R = 6378137;
  const lng = (mx / R) * (180 / Math.PI);
  const lat = (Math.atan(Math.exp(my / R)) * 2 - Math.PI / 2) * (180 / Math.PI);
  return [lat, lng];
}

// ── Parse GeoKey directory to get CRS EPSG code ─────────────────────────────
function getCrsEpsg(ifd) {
  const keyDir = ifd[TAG_GEO_KEY_DIRECTORY];
  if (!keyDir || keyDir.length < 4) return null;
  // Layout: [KeyDirectoryVersion, KeyRevision, MinorRevision, NumberOfKeys,
  //          KeyID, TIFFTagLocation, Count, Value_Offset, ...]
  const numKeys = keyDir[3];
  for (let k = 0; k < numKeys; k++) {
    const base = 4 + k * 4;
    const keyId = keyDir[base];
    const loc   = keyDir[base + 1];
    const val   = keyDir[base + 3];
    if (loc !== 0) continue; // value is inline only when loc === 0
    if (keyId === GEO_KEY_GEOGRAPHIC_TYPE || keyId === GEO_KEY_PROJECTED_CS_TYPE) {
      return val;
    }
  }
  return null;
}

// ── Extract geographic bounds from GeoTIFF IFD tags ─────────────────────────
function extractBounds(ifd) {
  try {
    const scale    = ifd[TAG_MODEL_PIXEL_SCALE];
    const tiepoint = ifd[TAG_MODEL_TIEPOINT];
    if (!scale || scale.length < 2 || !tiepoint || tiepoint.length < 6) return null;

    const scaleX = scale[0];
    const scaleY = scale[1]; // usually positive; Y in model coords decreases down
    const tpI = tiepoint[0]; const tpJ = tiepoint[1]; // raster pixel
    const tpX = tiepoint[3]; const tpY = tiepoint[4]; // model coordinate (X=lng, Y=lat for 4326)

    const width  = ifd.width;
    const height = ifd.height;

    // NW corner of the image in model coordinates
    const nwX = tpX - tpI * scaleX;
    const nwY = tpY + tpJ * scaleY;
    // SE corner
    const seX = nwX + width  * scaleX;
    const seY = nwY - height * scaleY;

    const epsg = getCrsEpsg(ifd);

    let west, east, north, south;
    if (epsg === 3857 || epsg === 900913) {
      [north, west] = mercatorToLatLng(nwX, nwY);
      [south, east] = mercatorToLatLng(seX, seY);
    } else {
      // Assume WGS84 (4326) or unknown — treat X as lng, Y as lat
      west = nwX; north = nwY;
      east = seX; south = seY;
    }

    if (
      !Number.isFinite(west)  || !Number.isFinite(east)  ||
      !Number.isFinite(south) || !Number.isFinite(north) ||
      Math.abs(west) > 180  || Math.abs(east) > 180  ||
      Math.abs(south) > 90  || Math.abs(north) > 90
    ) return null;

    return [[south, west], [north, east]];
  } catch (e) {
    return null;
  }
}

// ── Render IFD pixel data → RGBA canvas ──────────────────────────────────────
function ifdToCanvas(ifd) {
  const width  = ifd.width;
  const height = ifd.height;

  // UTIF.toRGBA8 converts any TIFF type/compression to a flat Uint8Array of RGBA
  const rgba = UTIF.toRGBA8(ifd);

  const canvas = document.createElement('canvas');
  canvas.width  = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const imgData = ctx.createImageData(width, height);
  imgData.data.set(rgba);
  ctx.putImageData(imgData, 0, 0);
  return canvas;
}

// ── Public API ────────────────────────────────────────────────────────────────
export async function loadTiff(file) {
  const arrayBuffer = await file.arrayBuffer();
  const ifds = UTIF.decode(arrayBuffer);
  if (!ifds || !ifds.length) throw new Error('No images found in TIFF file');

  // Decode the first image (largest overview)
  UTIF.decodeImage(arrayBuffer, ifds[0]);
  const ifd = ifds[0];

  const canvas = ifdToCanvas(ifd);
  const bounds = extractBounds(ifd);

  return { canvas, bounds, width: ifd.width, height: ifd.height };
}

export function isTiff(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  if (name.endsWith('.tif') || name.endsWith('.tiff')) return true;
  const type = String(file.type || '').toLowerCase();
  return type === 'image/tiff' || type === 'image/tif';
}
